<template>
  <ContentWrap>
    <!-- 搜索工作栏 -->
    <el-form
      ref="queryFormRef"
      :inline="true"
      :model="queryParams"
      class="-mb-15px"
      label-width="90px"
    >
      <el-form-item label="单号" prop="no">
        <el-input
          v-model="queryParams.no"
          class="!w-200px"
          clearable
          placeholder="请输入单号"
          @keyup.enter="handleQuery"
        />
      </el-form-item>
      <el-form-item label="客户" prop="customerId">
        <el-select
          v-model="queryParams.customerId"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择客户"
        >
          <el-option
            v-for="item in customerList"
            :key="item.id"
            :label="item.name"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进状态" prop="followStatus">
        <el-select
          v-model="queryParams.followStatus"
          class="!w-200px"
          clearable
          placeholder="请选择跟进状态"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进方式" prop="type">
        <el-select
          v-model="queryParams.type"
          class="!w-200px"
          clearable
          placeholder="请选择跟进方式"
        >
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.CRM_FOLLOW_UP_TYPE)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="showPersonFilter" label="人员" prop="creator">
        <el-select
          v-model="queryParams.creator"
          class="!w-200px"
          clearable
          filterable
          placeholder="请选择申请人"
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="跟进时间" prop="followTimes">
        <el-date-picker
          v-model="queryParams.followTimes"
          :default-time="defaultTime"
          class="!w-360px"
          end-placeholder="结束日期"
          range-separator="-"
          start-placeholder="开始日期"
          type="daterange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item>
        <el-button @click="handleQuery">
          <Icon class="mr-5px" icon="ep:search" />
          搜索
        </el-button>
        <el-button @click="resetQuery">
          <Icon class="mr-5px" icon="ep:refresh" />
          重置
        </el-button>
        <el-button
          v-if="showCreate"
          v-hasPermi="['crm:follow-up-record:create']"
          type="primary"
          @click="openForm('create')"
        >
          <Icon class="mr-5px" icon="ep:plus" />
          新增
        </el-button>
        <el-button
          v-hasPermi="['crm:follow-up-record:export']"
          :loading="exportLoading"
          plain
          type="success"
          @click="handleExport"
        >
          <Icon class="mr-5px" icon="ep:download" />
          导出
        </el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <!-- 列表 -->
  <ContentWrap>
    <el-tabs v-if="showTabs" v-model="activeName" @tab-click="handleTabClick">
      <el-tab-pane label="我负责的" name="1" />
      <el-tab-pane label="我参与的" name="2" />
      <el-tab-pane label="我下属参与的" name="3" />
    </el-tabs>

    <el-table v-loading="loading" :data="list" :show-overflow-tooltip="true" :stripe="true">
      <el-table-column align="center" fixed="left" label="客户" prop="customerName" width="160" />
      <el-table-column align="center" label="联系人" prop="contacts" width="140">
        <template #default="scope">
          <span v-for="(contact, index) in scope.row.contacts" :key="contact.id">
            <el-link
              :underline="false"
              class="ml-5px"
              type="primary"
              @click="openContactDetail(contact.id)"
            >
              {{ contact.name }}
            </el-link>
            <span v-if="index < scope.row.contacts.length - 1">、</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column align="center" label="跟进内容" prop="content" width="240" />
      <!-- ★ 列顺序以原型图为准：「跟进状态」在「跟进方式」之前，不要对调 -->
      <el-table-column align="center" label="跟进状态" prop="followStatus" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_FOLLOW_UP_STATUS" :value="scope.row.followStatus" />
        </template>
      </el-table-column>
      <el-table-column align="center" label="跟进方式" prop="type" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.CRM_FOLLOW_UP_TYPE" :value="scope.row.type" />
        </template>
      </el-table-column>
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="上次跟进时间"
        prop="followTime"
        width="180"
      />
      <el-table-column
        :formatter="dateFormatter"
        align="center"
        label="下次跟进时间"
        prop="nextTime"
        width="180"
      />
      <el-table-column align="center" label="跟进备注" prop="remark" width="180" />
      <el-table-column align="center" label="申请人" prop="creatorName" width="110" />
      <el-table-column align="center" label="申请人部门" prop="creatorDeptName" width="130" />
      <el-table-column align="center" label="单号" prop="no" width="170" />
      <el-table-column
        align="center"
        fixed="right"
        :show-overflow-tooltip="false"
        label="操作"
        width="120"
      >
        <template #default="scope">
          <div class="crm-followup-action">
            <el-button
              v-hasPermi="['crm:follow-up-record:update']"
              link
              type="primary"
              @click="openForm('update', scope.row.id)"
            >
              编辑
            </el-button>
            <el-button
              v-hasPermi="['crm:follow-up-record:delete']"
              link
              type="danger"
              @click="handleDelete(scope.row.id)"
            >
              删除
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <!-- 分页 -->
    <Pagination
      v-model:limit="queryParams.pageSize"
      v-model:page="queryParams.pageNo"
      :total="total"
      @pagination="getList"
    />
  </ContentWrap>

  <!-- 表单弹窗：添加/修改 -->
  <FollowUpEditForm
    ref="formRef"
    :lock-customer="!!customerId"
    :customer-id="customerId"
    :customer-name="customerName"
    @success="getList"
  />
</template>

<script lang="ts" setup>
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'
import { dateFormatter } from '@/utils/formatTime'
import download from '@/utils/download'
import { FollowUpRecordApi, FollowUpRecordVO } from '@/api/crm/followup'
import * as CustomerApi from '@/api/crm/customer'
import * as UserApi from '@/api/system/user'
import { BizTypeEnum } from '@/api/crm/permission'
import { TabsPaneContext } from 'element-plus'
import FollowUpEditForm from './FollowUpEditForm.vue'

const props = withDefaults(
  defineProps<{
    /** 是否展示「我负责的 / 我参与的 / 我下属参与的」3 个 tab */
    showTabs?: boolean
    /** 是否展示「新增」按钮 */
    showCreate?: boolean
    /**
     * 所属客户编号（可选）。
     * ★ 挂在客户详情页时必须传：本壳的「新增」按钮调用的表单 `FollowUpEditForm`
     *   硬编码 `bizType = CRM_CUSTOMER` 且**客户必填**，而壳自己又拿不到「当前是哪个客户」，
     *   若不传 ⇒ `openForm('create')` 不预置客户 ⇒ 客户框空白、提交被拦。
     * 列表查询仍走 `queryParams.bizType / bizId`（由 `biz-id`/`biz-type` 决定），本 prop 只影响新增弹窗。
     */
    customerId?: number
    /** 所属客户名称（可选，配合 `customerId` 一起传），用于让表单客户下拉零请求解析出名称 */
    customerName?: string
  }>(),
  {
    showTabs: false,
    showCreate: true
  }
)

const message = useMessage() // 消息弹窗
const { t } = useI18n() // 国际化

const loading = ref(true) // 列表的加载中
const total = ref(0) // 列表的总页数
const list = ref<FollowUpRecordVO[]>([]) // 列表的数据
const customerList = ref<any[]>([]) // 客户下拉
const userOptions = ref<UserApi.UserVO[]>([]) // 用户下拉（人员筛选）
const exportLoading = ref(false) // 导出的加载中
const activeName = ref('1') // 列表 tab
/** 日期范围默认带时分秒 00:00:00 ~ 23:59:59 */
const defaultTime = [new Date(2000, 0, 1, 0, 0, 0), new Date(2000, 0, 1, 23, 59, 59)]

const queryParams = reactive<any>({
  pageNo: 1,
  pageSize: 10,
  // 客户跟进固定传 2（CRM_CUSTOMER）
  bizType: BizTypeEnum.CRM_CUSTOMER,
  // ★ 必须与 activeName 的初值 '1' 保持一致。若留 undefined，qs 序列化会**丢弃该参数**，
  //   后端 sceneType=null 即「不做数据权限过滤」，会把别人的跟进记录也查出来。
  //   全部跟进（showTabs=false）不传 sceneType，后端即返回全量。
  sceneType: props.showTabs ? '1' : undefined,
  no: '',
  customerId: undefined,
  followStatus: undefined,
  type: undefined,
  creator: undefined,
  followTimes: []
})
const queryFormRef = ref() // 搜索的表单

/** 是否展示「人员」筛选：全部跟进常显；我的跟进仅「我下属参与的」tab 显示（对齐原型） */
const showPersonFilter = computed(() => !props.showTabs || activeName.value === '3')

/** tab 切换 */
const handleTabClick = (tab: TabsPaneContext) => {
  const paneName = tab.paneName as string
  activeName.value = paneName
  queryParams.sceneType = paneName
  // 切换到非下属 tab 时清掉人员筛选，避免与场景口径打架
  if (paneName !== '3') {
    queryParams.creator = undefined
  }
  handleQuery()
}

/** 查询列表 */
const getList = async () => {
  loading.value = true
  try {
    const data = await FollowUpRecordApi.getFollowUpRecordPage(queryParams)
    list.value = data.list
    total.value = data.total
  } finally {
    loading.value = false
  }
}

/** 搜索按钮操作 */
const handleQuery = () => {
  queryParams.pageNo = 1
  getList()
}

/** 重置按钮操作 */
const resetQuery = () => {
  queryFormRef.value.resetFields()
  queryParams.no = ''
  queryParams.customerId = undefined
  queryParams.followStatus = undefined
  queryParams.type = undefined
  queryParams.creator = undefined
  queryParams.followTimes = []
  handleQuery()
}

/** 添加/修改操作 */
const formRef = ref<InstanceType<typeof FollowUpEditForm>>()
const openForm = (type: string, id?: number) => {
  // ★ 新增时必须把「当前客户」传给表单：表单的客户字段是**必填**，
  //   不预置就会得到一个空白的必填客户框（提交被 `customerId 不能为空` 拦住）。
  //   编辑（传了 id）不需要：客户由该条跟进记录自身带出。
  if (type === 'create') {
    formRef.value?.open('create', undefined, props.customerId, props.customerName)
    return
  }
  formRef.value?.open(type, id)
}

/** 删除按钮操作 */
const handleDelete = async (id: number) => {
  try {
    // 删除的二次确认
    await message.delConfirm()
    // 发起删除
    await FollowUpRecordApi.deleteFollowUpRecord(id)
    message.success(t('common.delSuccess'))
    // 刷新列表
    await getList()
  } catch {}
}

/** 打开联系人详情 */
const { push } = useRouter()
const openContactDetail = (id: number) => {
  push({ name: 'CrmContactDetail', params: { id } })
}

/** 导出按钮操作 */
const handleExport = async () => {
  try {
    // 导出的二次确认
    await message.exportConfirm()
    // 发起导出
    exportLoading.value = true
    const data = await FollowUpRecordApi.exportFollowUpRecord(queryParams)
    download.excel(data, '跟进记录.xlsx')
  } catch {
  } finally {
    exportLoading.value = false
  }
}

/** 初始化 **/
onMounted(async () => {
  customerList.value = await CustomerApi.getCustomerSimpleList()
  userOptions.value = await UserApi.getSimpleUserList()
  await getList()
})
</script>

<style lang="scss" scoped>
/**
 * 操作列对齐：统一交给 flex 垂直居中，并把 el-button 默认的 margin-left 换成 gap，
 * 避免按钮之间的间距不一致。
 */
.crm-followup-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  :deep(.el-button + .el-button) {
    margin-left: 0;
  }
}
</style>
